import type { CategoryBudget, CategoryId, ContractDocument } from './contract-document.js';
import {
  CATEGORY_IDS,
  DEFAULT_CONTINGENCY_SHARE,
  categoryCarriesCost,
  getCategory,
} from './contract-document.js';
import type { CostRatePeriod, PersonRef, RoleRef } from './external-refs.js';
import type { PlanNode, ProjectDocument } from './project-document.js';
import { walkPlan } from './project-document.js';

/**
 * Every derived number in the module, as pure functions over loaded documents.
 *
 * Numbered rules (CALC-xx) are specified in docs/specs/calculations.md — the
 * worked examples there are reproduced digit-for-digit by the unit tests.
 * Nothing here is ever persisted: base data in, derived numbers out.
 */

// ─── Inputs ───

/** A contract as loaded from its aggregate row. */
export type ContractInput = {
  id: string;
  name: string;
  type: 'fixed' | 'tm' | 'retainer';
  document: ContractDocument;
};

/** Realized hours per plan-node id, joined from synced actuals (BR-22). */
export type RealizedByNode = Record<string, number>;

/**
 * Options derived from the budget-model config (ADR 0027) — see
 * `calcOptionsOf` in budget-model.ts. Defaults preserve pre-config behaviour:
 * every category available, per-section factors on. The summary endpoint is
 * deliberately config-agnostic and never passes them.
 */
export type BudgetCalcOptions = {
  /** CALC-19 — categories flagged false do not exist for this tenant (BR-27). */
  availability?: Partial<Record<CategoryId, boolean>>;
  /**
   * CALC-20 — sections whose uplift the user cannot edit, pinned to the
   * config's factor for that section. A section absent from this map uses its
   * stored `targetFactor`; an absent map pins nothing.
   */
  fixedFactors?: Partial<Record<CategoryId, number>>;
};

const isAvailable = (id: CategoryId, options?: BudgetCalcOptions): boolean =>
  options?.availability?.[id] !== false;

// ─── Plan side ───

const isLeaf = (node: PlanNode): boolean => node.children.length === 0;

/**
 * BR-4 — the contract a leaf is mapped to. Explicit only: absent means no
 * contract, and nothing falls back to a project default or a parent (D8).
 */
export const mappedContractId = (node: PlanNode): string | undefined => node.contractId;

/**
 * CALC-1 — a leaf's BUDGET hours.
 *
 * Revised (BR-28, ADR 0030): the budget is `estimatedHours`, full stop.
 * Allocations no longer replace it — they are a second, independent quantity
 * (CALC-22) that the budget is compared against. The plan's hours are what the
 * work is worth; who is committed to it is a different question, and the money
 * (CALC-2) is the one number that follows the staffing rather than the budget.
 */
export const leafHours = (node: PlanNode): number => node.estimatedHours ?? 0;

/**
 * CALC-22 — a leaf's ALLOCATED hours: Σ over its staffing lines.
 *
 * Every stored line names a role (BR-29 — a draft without one lives in the
 * editor only and never reaches the document), so every line counts. Compare
 * with CALC-1, never reconcile: a disagreement is the signal the row, the
 * overlay footer and the panel's track all render.
 */
export const leafAllocatedHours = (node: PlanNode): number =>
  node.allocations.reduce((sum, allocation) => sum + allocation.hours, 0);

// ─── Pricing (CALC-23) ───

/** D13 — how allocation revenue is derived under a fixed-price contract. */
export const COMPLETION_PRINCIPLES = ['WORK_COMPLETED', 'COST_TO_COST'] as const;
export type CompletionPrinciple = (typeof COMPLETION_PRINCIPLES)[number];

/**
 * The principle that applies when a caller passes none (BR-30, ADR 0041):
 * WORK_COMPLETED prices fixed-price hours at the contract's average rate;
 * COST_TO_COST derives revenue from cost and is reserved — it prices nothing
 * yet. The editor reads the configured value from the budget model
 * (`planCalcOptionsOf`); the API's summary deliberately passes nothing and so
 * prices under this default (ADR 0027).
 */
export const DEFAULT_COMPLETION_PRINCIPLE: CompletionPrinciple = 'WORK_COMPLETED';

/** Options for the plan-side numbers: the budget-model ones plus the pricing inputs. */
export type PlanCalcOptions = BudgetCalcOptions & {
  /**
   * The ISO calendar day time-phased cost rates resolve against (D14). Injected
   * by the caller — never a local Date inside a pure function. Absent = the
   * open-ended period applies.
   */
  today?: string;
  completionPrinciple?: CompletionPrinciple;
};

/**
 * CALC-23 — a fixed-price contract's average sell rate: Own Work amount over
 * Own Work hours. Every hour on the contract prices at it, whatever the role
 * (D12). Zero hours is the user's number (D11) and prices at 0.
 */
export const avgRate = (
  document: ContractDocument,
  blendedCostRate: number,
  options?: BudgetCalcOptions,
): number => {
  const hours = ownHours(document, blendedCostRate, options);
  return hours > 0 ? getCategory(document, 'own').amount / hours : 0;
};

/**
 * CALC-23 — the cost per hour a fixed-price contract's own numbers imply: Own
 * Work cost over Own Work hours. `factor = avgRate / impliedCostRate` holds by
 * construction — amount, factor and hours are user-owned, this is the derived
 * one (D11). Undefined at zero hours.
 */
export const impliedCostRate = (
  document: ContractDocument,
  blendedCostRate: number,
  options?: BudgetCalcOptions,
): number | undefined => {
  const hours = ownHours(document, blendedCostRate, options);
  return hours > 0 ? categoryCost(getCategory(document, 'own'), options) / hours : undefined;
};

/**
 * The rate valid on `today` (D4/D14): the period containing the day; before the
 * first period, the first period's rate; the last period is open-ended, so
 * coverage is total. ISO days compare as strings. Undefined only for an empty
 * schedule; with no `today` the open-ended period applies.
 */
export const costRateValidOn = (
  periods: readonly CostRatePeriod[],
  today: string | undefined,
): number | undefined => {
  if (periods.length === 0) return undefined;
  const sorted = [...periods].sort((a, b) => (a.validFrom ?? '').localeCompare(b.validFrom ?? ''));
  if (today === undefined) return sorted[sorted.length - 1]!.costRate;
  let current = sorted[0]!;
  for (const period of sorted) {
    if (period.validFrom === null || period.validFrom <= today) current = period;
    else break;
  }
  return current.costRate;
};

/**
 * How a line's sell rate resolved (D7, D15). `unpriced` is NOT a number — no
 * contract to price against, rendered as "—". `missing-role` is a real 0 with
 * a warning: a T&M card that lacks the role (BR-11). `derived` is a real
 * number that was not agreed with anyone: Cost-To-Cost revenue, the line's
 * cost rate × the Own Work factor (CALC-24) — shown, and said to be derived.
 */
export type SellRateState = 'priced' | 'unpriced' | 'missing-role' | 'derived';
export type SellRate = { rate: number; state: SellRateState };
/**
 * Resolves per LINE, not per role id: Cost-To-Cost prices from the line's
 * cost, and that is the person's when one is named, the role's otherwise.
 */
export type SellRateResolver = (
  contractId: string | undefined,
  role: RoleRef | undefined,
  person?: PersonRef,
) => SellRate;

const UNPRICED: SellRate = { rate: 0, state: 'unpriced' };

/**
 * CALC-2 / CALC-23 / CALC-24 — the sell-rate matrix (D15), contract type ×
 * completion principle:
 *   no contract               → unpriced
 *   T&M                       → the card's amount for the role; missing → 0 + warning
 *   fixed, Work Completed     → avgRate, any role
 *   fixed, Cost-To-Cost       → the line's cost rate (CALC-21 chain) × the Own
 *                               Work factor as the card shows it (CALC-20) — `derived`
 */
export const createSellRateResolver = (
  contracts: ContractInput[],
  blendedCostRate: number,
  options?: PlanCalcOptions,
  warnings?: Set<string>,
): SellRateResolver => {
  const byId = new Map(contracts.map((contract) => [contract.id, contract]));
  const cards = new Map<string, Map<string, number>>(
    contracts.map((contract) => [
      contract.id,
      new Map(contract.document.rateCard.map((entry) => [entry.roleRef.tlpId, entry.rate])),
    ]),
  );
  const principle = options?.completionPrinciple ?? DEFAULT_COMPLETION_PRINCIPLE;
  const resolveCostRate = createCostRateResolver(contracts, blendedCostRate, options);

  // Warnings are deduplicated (a Set) — with ten thousand tasks, per-occurrence
  // messages would drown the response.
  return (contractId, role, person) => {
    const roleServiceId = role?.tlpId;
    const contract = contractId === undefined ? undefined : byId.get(contractId);
    if (!contract) {
      warnings?.add('No contract to resolve a rate from');
      return UNPRICED;
    }
    if (contract.type === 'fixed') {
      if (principle === 'COST_TO_COST') {
        // CALC-24: revenue follows cost. The factor is the Own Work card's —
        // the PM's override when uplift is on, 1 when the variant pins it.
        const factor = effectiveFactor(getCategory(contract.document, 'own'), options);
        return {
          rate: resolveCostRate(contractId, role, person) * factor,
          state: 'derived',
        };
      }
      return { rate: avgRate(contract.document, blendedCostRate, options), state: 'priced' };
    }
    if (!roleServiceId) {
      warnings?.add('No role to resolve a rate from');
      return { rate: 0, state: 'missing-role' };
    }
    const rate = cards.get(contract.id)?.get(roleServiceId);
    if (rate === undefined) {
      warnings?.add(`Role '${roleServiceId}' is not on the rate card of contract ${contract.id}`);
      return { rate: 0, state: 'missing-role' };
    }
    return { rate, state: 'priced' };
  };
};

export type CostRateResolver = (
  contractId: string | undefined,
  role: RoleRef | undefined,
  person?: PersonRef,
) => number;

/**
 * CALC-21 — the cost chain (D14, extended by ADR 0042), first match wins:
 *   a named person          → the employee's cost rate valid today (snapshotted schedule)
 *   role only, T&M          → the card's costRate for the role, else the role snapshot's
 *   role only, fixed price  → the role snapshot's cost price, else the contract's
 *                             implied cost per hour (CALC-23)
 *   role only, no contract  → the role snapshot's cost price
 *   anything else           → the project's blended rate
 * The role snapshot's cost price is what the pick captured (`RoleRef.snapshot.costRate`);
 * the T&M card's entry outranks it because the PM can edit the card.
 *
 * Work never costs nothing (2026-10-12): a cost rate of 0 at any step — a
 * person's schedule, a card entry, a role snapshot, the implied rate — is
 * treated as unknown and the chain moves on, down to the blended rate.
 */
const knownCost = (rate: number | undefined): number | undefined =>
  rate !== undefined && rate > 0 ? rate : undefined;

export const createCostRateResolver = (
  contracts: ContractInput[],
  blendedCostRate: number,
  options?: PlanCalcOptions,
): CostRateResolver => {
  const byId = new Map(contracts.map((contract) => [contract.id, contract]));
  const cards = new Map<string, Map<string, number | undefined>>(
    contracts.map((contract) => [
      contract.id,
      new Map(contract.document.rateCard.map((entry) => [entry.roleRef.tlpId, entry.costRate])),
    ]),
  );
  return (contractId, role, person) => {
    const personRate = knownCost(
      person?.snapshot.costRates === undefined
        ? undefined
        : costRateValidOn(person.snapshot.costRates, options?.today),
    );
    if (personRate !== undefined) return personRate;

    const roleSnapshotRate = knownCost(role?.snapshot.costRate);
    const contract = contractId === undefined ? undefined : byId.get(contractId);
    if (!contract) return roleSnapshotRate ?? blendedCostRate;
    if (contract.type === 'fixed') {
      return (
        roleSnapshotRate ??
        knownCost(impliedCostRate(contract.document, blendedCostRate, options)) ??
        blendedCostRate
      );
    }
    const cardRate = knownCost(
      role === undefined ? undefined : cards.get(contract.id)?.get(role.tlpId),
    );
    return cardRate ?? roleSnapshotRate ?? blendedCostRate;
  };
};

/** CALC-2 — leaf planned revenue. Non-billable leaves contribute 0 (BR-5). */
export const leafAmount = (node: PlanNode, resolveRate: SellRateResolver): number => {
  if (node.billable === false) return 0;
  const contractId = mappedContractId(node);

  /*
    Per allocation, no blending. Once a task is staffed the allocations ARE
    the money, so a 120 h budget with one 80 h allocation prices at 80 h: the
    plan is what is staffed (BR-28). Unstaffed, it falls back to the budget at
    the task's own role rate.
  */
  if (node.allocations.length > 0) {
    return node.allocations.reduce(
      (sum, allocation) =>
        sum +
        allocation.hours * resolveRate(contractId, allocation.roleRef, allocation.personRef).rate,
      0,
    );
  }
  return (node.estimatedHours ?? 0) * resolveRate(contractId, node.roleRef, node.assignee).rate;
};

export type NodeRollup = {
  /** CALC-1/CALC-3 — budget hours, rolled up over leaf descendants. */
  hours: number;
  /** CALC-22/CALC-3 — allocated hours, same rollup. */
  allocatedHours: number;
  amount: number;
  realized: number;
  /** CALC-5 */
  realizedPct: number;
  /** CALC-7 — % of total plan hours. */
  shareOfPlanPct: number;
};

/** The one accumulator the post-order pass carries: both dimensions plus money. */
type RollupSum = Pick<NodeRollup, 'hours' | 'allocatedHours' | 'amount' | 'realized'>;

export type PlanRollup = {
  /** CALC-3 per node (keyed by node id), computed over leaf descendants. */
  nodes: Record<string, NodeRollup>;
  /** CALC-4 overall totals. */
  totals: { hours: number; amount: number; realized: number; realizedPct: number };
  /** CALC-8 */
  perDeliverable: {
    id: string;
    code?: string;
    name: string;
    hours: number;
    amount: number;
    sharePct: number;
  }[];
  /** CALC-4 consumption inputs, keyed by effective contract id. */
  consumedByContract: Record<string, { hours: number; amount: number; cost: number }>;
  /** Hours on leaves with no resolvable contract (excluded from consumption). */
  unassignedHours: number;
  warnings: string[];
};

/** CALC-1..8 — the whole-plan rollup in one pass. */
export const rollupPlan = (
  project: ProjectDocument,
  contracts: ContractInput[],
  realizedByNode: RealizedByNode = {},
  options?: PlanCalcOptions,
): PlanRollup => {
  const warningSet = new Set<string>();
  const resolveRate = createSellRateResolver(
    contracts,
    project.blendedCostRate,
    options,
    warningSet,
  );
  const resolveCostRate = createCostRateResolver(contracts, project.blendedCostRate, options);
  const knownContracts = new Set(contracts.map((contract) => contract.id));
  let unassignedTaskCount = 0;

  const nodes: Record<string, NodeRollup> = {};
  const consumedByContract: Record<string, { hours: number; amount: number; cost: number }> = {};
  let unassignedHours = 0;

  // CALC-21 — a leaf's planned cost, independent of billability (cost is spent
  // either way). A staffing line's person, or the estimate line's assignee,
  // carries their own cost rate (D14).
  const leafCost = (node: PlanNode, contractId: string | undefined): number =>
    node.allocations.length > 0
      ? node.allocations.reduce(
          (sum, allocation) =>
            sum +
            allocation.hours *
              resolveCostRate(contractId, allocation.roleRef, allocation.personRef),
          0,
        )
      : (node.estimatedHours ?? 0) * resolveCostRate(contractId, node.roleRef, node.assignee);

  const rollupLeaf = (node: PlanNode): RollupSum => {
    const hours = leafHours(node);
    const allocatedHours = leafAllocatedHours(node);
    const amount = leafAmount(node, resolveRate);
    const realized = realizedByNode[node.id] ?? 0;

    /*
      Every leaf's hours consume a contract (CALC-4/CALC-15) — including a
      childless root node, which ADR 0028 lets carry its own estimate and
      contract. This used to test `kind === 'task'`, safe only while roots were
      barred from holding hours: past that, a root's hours reached the plan
      total but not the consumption buckets, so plan-vs-budget silently
      under-counted. `rollupLeaf` is only ever called on leaves, so no kind
      test is needed. A leaf with no contract is unassigned: its hours count,
      its revenue is nothing (D7).
    */
    const contractId = mappedContractId(node);
    if (contractId && knownContracts.has(contractId)) {
      const bucket = (consumedByContract[contractId] ??= { hours: 0, amount: 0, cost: 0 });
      bucket.hours += hours;
      bucket.amount += amount;
      bucket.cost += leafCost(node, contractId);
    } else {
      unassignedHours += hours;
      if (hours > 0) unassignedTaskCount++;
    }
    return { hours, allocatedHours, amount, realized };
  };

  // CALC-3: a node's rollup sums its leaf descendants. Iterative post-order —
  // plans can be arbitrarily deep (BR-3), so no recursion.
  const zero = (): RollupSum => ({ hours: 0, allocatedHours: 0, amount: 0, realized: 0 });
  const totals = zero();
  type Frame = {
    node: PlanNode;
    childIndex: number;
    sum: RollupSum;
    parent: Frame | undefined;
  };
  const addTo = (target: RollupSum, value: RollupSum) => {
    target.hours += value.hours;
    target.allocatedHours += value.allocatedHours;
    target.amount += value.amount;
    target.realized += value.realized;
  };

  const stack: Frame[] = [];
  for (const root of project.plan) {
    stack.push({
      node: root,
      childIndex: 0,
      sum: zero(),
      parent: undefined,
    });

    while (stack.length > 0) {
      const frame = stack[stack.length - 1]!;

      if (frame.childIndex < frame.node.children.length) {
        const child = frame.node.children[frame.childIndex++]!;
        stack.push({
          node: child,
          childIndex: 0,
          sum: zero(),
          parent: frame,
        });
        continue;
      }

      stack.pop();
      const sum = isLeaf(frame.node) ? rollupLeaf(frame.node) : frame.sum;
      nodes[frame.node.id] = { ...sum, realizedPct: 0, shareOfPlanPct: 0 };
      addTo(frame.parent ? frame.parent.sum : totals, sum);
    }
  }

  if (unassignedTaskCount > 0) {
    warningSet.add(`${unassignedTaskCount} task(s) have no resolvable contract`);
  }
  const warnings = [...warningSet];

  // Second pass: percentages need the totals.
  for (const rollup of Object.values(nodes)) {
    rollup.realizedPct = rollup.hours > 0 ? Math.round((rollup.realized / rollup.hours) * 100) : 0;
    rollup.shareOfPlanPct = totals.hours > 0 ? Math.round((rollup.hours / totals.hours) * 100) : 0;
  }

  const perDeliverable = project.plan.map((root) => ({
    id: root.id,
    ...(root.code === undefined ? {} : { code: root.code }),
    name: root.name,
    hours: nodes[root.id]?.hours ?? 0,
    amount: nodes[root.id]?.amount ?? 0,
    sharePct:
      totals.hours > 0 ? Math.round(((nodes[root.id]?.hours ?? 0) / totals.hours) * 100) : 0,
  }));

  return {
    nodes,
    totals: {
      hours: totals.hours,
      amount: totals.amount,
      realized: totals.realized,
      realizedPct: totals.hours > 0 ? Math.round((totals.realized / totals.hours) * 100) : 0,
    },
    perDeliverable,
    consumedByContract,
    unassignedHours,
    warnings,
  };
};

/** CALC-6 — days-left signal for a target date. Pure: `today` is an ISO date string. */
export type DaysLeftInfo = {
  days: number | null;
  bucket: 'none' | 'over' | 'due-today' | 'soon' | 'later';
};

export const daysLeft = (targetDate: string | undefined, today: string): DaysLeftInfo => {
  if (!targetDate) return { days: null, bucket: 'none' };
  const ms = Date.parse(targetDate) - Date.parse(today);
  const days = Math.round(ms / 86_400_000);
  if (days < 0) return { days, bucket: 'over' };
  if (days === 0) return { days, bucket: 'due-today' };
  if (days <= 3) return { days, bucket: 'soon' };
  return { days, bucket: 'later' };
};

// ─── Contract side ───

/**
 * A category participates in this contract's distribution unless hidden, moved
 * (BR-10) or unavailable under the budget-model config (CALC-19 / BR-27).
 */
const isDistributed = (category: CategoryBudget, options?: BudgetCalcOptions): boolean =>
  !category.hidden && !category.billedVia && isAvailable(category.id, options);

/** CALC-20 — the operative factor: stored, or the config's when it is pinned. */
const effectiveFactor = (category: CategoryBudget, options?: BudgetCalcOptions): number =>
  options?.fixedFactors?.[category.id] ?? category.targetFactor;

/**
 * CALC-9 (CALC-20-aware). A cost-less category — Contingency (BR-14) — costs
 * nothing whatever its stored factor: its amount is all margin.
 */
export const categoryCost = (category: CategoryBudget, options?: BudgetCalcOptions): number =>
  categoryCarriesCost(category.id) ? category.amount / effectiveFactor(category, options) : 0;

/** CALC-10 — the only margin formula. */
export const margin = (factor: number): number => 1 - 1 / factor;

/**
 * CALC-11 — Own Work hours. Once the user has typed them they are the user's
 * number and are never recalculated (sticky, D11); until then they derive from
 * Own Work cost at the project's blended rate — the seed, nothing more.
 */
export const ownHours = (
  document: ContractDocument,
  blendedCostRate: number,
  options?: BudgetCalcOptions,
): number => {
  const own = getCategory(document, 'own');
  if (own.ownHoursOverride !== undefined) return own.ownHoursOverride;
  return Math.round(categoryCost(own, options) / blendedCostRate);
};

/** CALC-12 — the one contract-cost definition. */
export const contractCost = (document: ContractDocument, options?: BudgetCalcOptions): number =>
  document.categories
    .filter((category) => isDistributed(category, options))
    .reduce((sum, category) => sum + categoryCost(category, options), 0);

/** CALC-14 — derived, never stored. Negative = over-distributed. */
export const notAllocated = (document: ContractDocument, options?: BudgetCalcOptions): number =>
  document.revenue -
  document.categories
    .filter((category) => isDistributed(category, options))
    .reduce((sum, category) => sum + category.amount, 0);

export type CategoryFinancials = {
  id: CategoryId;
  amount: number;
  targetFactor: number;
  cost: number;
  marginPct: number;
  sharePct: number;
  hidden: boolean;
  /** CALC-19 — false when the budget-model config removes the category (BR-27). */
  available: boolean;
  billedVia?: string;
};

export type ContractFinancials = {
  revenue: number;
  cost: number;
  /** CALC-13; null while the contract has no cost. */
  budgetedFactor: number | null;
  marginPct: number | null;
  notAllocated: number;
  overDistributed: boolean;
  ownHours: number;
  categories: CategoryFinancials[];
};

/** CALC-9..14 for one contract (CALC-19/20-aware via options). */
export const calculateContractFinancials = (
  document: ContractDocument,
  blendedCostRate: number,
  options?: BudgetCalcOptions,
): ContractFinancials => {
  const cost = contractCost(document, options);
  const factor = cost > 0 ? document.revenue / cost : null;
  const na = notAllocated(document, options);

  return {
    revenue: document.revenue,
    cost,
    budgetedFactor: factor,
    marginPct: factor ? Math.round(margin(factor) * 100) : null,
    notAllocated: na,
    overDistributed: na < 0,
    ownHours: ownHours(document, blendedCostRate, options),
    categories: CATEGORY_IDS.map((id) => {
      const category = getCategory(document, id);
      const operativeFactor = effectiveFactor(category, options);
      return {
        id,
        amount: category.amount,
        targetFactor: category.targetFactor,
        cost: isDistributed(category, options) ? categoryCost(category, options) : 0,
        // A cost-less category is all margin (CALC-9/BR-14); its factor is inert.
        marginPct: categoryCarriesCost(id) ? Math.round(margin(operativeFactor) * 100) : 100,
        sharePct: document.revenue > 0 ? (category.amount / document.revenue) * 100 : 0,
        hidden: category.hidden,
        available: isAvailable(id, options),
        ...(category.billedVia === undefined ? {} : { billedVia: category.billedVia }),
      };
    }),
  };
};

/** CALC-17 — informational split amounts (BR-19). */
export type SplitAmounts = {
  rows: { id: string; name: string; sharePct: number; amount: number }[];
  overAllocated: boolean;
};

export const splitAmounts = (category: CategoryBudget): SplitAmounts => {
  if (!category.split) return { rows: [], overAllocated: false };

  const parentAmount = category.amount;
  const entries =
    category.split.kind === 'rows'
      ? category.split.rows
      : [...category.split.suppliers, ...category.split.unassigned];

  return {
    rows: entries.map((row) => ({
      id: row.id,
      name: row.name,
      sharePct: row.sharePct,
      amount: (parentAmount * row.sharePct) / 100,
    })),
    overAllocated: entries.reduce((sum, row) => sum + row.sharePct, 0) > 100,
  };
};

// ─── The carry-over: contract budget vs plan consumption ───

export type ContractConsumption = {
  contractId: string;
  name: string;
  type: ContractInput['type'];
  /** CALC-15 — the hours budget the plan consumes. */
  hoursBudget: number;
  /** CALC-15 — the contract's revenue incl. T&M extras (v8: the plan panel's budget line). */
  revenueBudget: number;
  /** The Own Work card's revenue-basis denominator (its amount). */
  ownRevenueBudget: number;
  /**
   * CALC-21 — the Own Work card's cost-basis denominator: hours × the contract's
   * own cost per hour — the implied rate on fixed price (CALC-23), the blended
   * rate on T&M.
   */
  ownCostBudget: number;
  consumedHours: number;
  consumedAmount: number;
  /** CALC-21 — planned cost against this contract (cost rates, blended fallback). */
  consumedCost: number;
  remainingHours: number;
  remainingAmount: number;
};

/** CALC-15 — per contract, from one plan rollup. */
export const calculateConsumption = (
  contracts: ContractInput[],
  project: ProjectDocument,
  planRollup: PlanRollup,
  options?: PlanCalcOptions,
): ContractConsumption[] =>
  contracts.map((contract) => {
    const consumed = planRollup.consumedByContract[contract.id] ?? {
      hours: 0,
      amount: 0,
      cost: 0,
    };
    const hoursBudget = ownHours(contract.document, project.blendedCostRate, options);
    const revenueBudget = contract.document.revenue;
    const ownCostRate =
      contract.type === 'fixed'
        ? (impliedCostRate(contract.document, project.blendedCostRate, options) ??
          project.blendedCostRate)
        : project.blendedCostRate;

    return {
      contractId: contract.id,
      name: contract.name,
      type: contract.type,
      hoursBudget,
      revenueBudget,
      ownRevenueBudget: getCategory(contract.document, 'own').amount,
      ownCostBudget: Math.round(hoursBudget * ownCostRate),
      consumedHours: consumed.hours,
      consumedAmount: consumed.amount,
      consumedCost: consumed.cost,
      remainingHours: hoursBudget - consumed.hours,
      remainingAmount: revenueBudget - consumed.amount,
    };
  });

export type ProjectFinancials = {
  contractCount: number;
  revenue: number;
  cost: number;
  budgetedFactor: number | null;
  marginPct: number | null;
  /** Σ own hours — the project's hours budget. */
  ownHours: number;
  ownRevenue: number;
  avgRate: number | null;
  /** v1: forecast mirrors budget (deck: "for now"). */
  forecastedFactor: number | null;
};

/** CALC-16 — the project rollup across contracts. Assumes one currency (BR-18). */
export const calculateProjectFinancials = (
  contracts: ContractInput[],
  blendedCostRate: number,
  options?: BudgetCalcOptions,
): ProjectFinancials => {
  const revenue = contracts.reduce((sum, contract) => sum + contract.document.revenue, 0);
  const cost = contracts.reduce(
    (sum, contract) => sum + contractCost(contract.document, options),
    0,
  );
  const hours = contracts.reduce(
    (sum, contract) => sum + ownHours(contract.document, blendedCostRate, options),
    0,
  );
  const ownRevenue = contracts.reduce(
    (sum, contract) => sum + getCategory(contract.document, 'own').amount,
    0,
  );
  const factor = cost > 0 ? revenue / cost : null;

  return {
    contractCount: contracts.length,
    revenue,
    cost,
    budgetedFactor: factor,
    marginPct: factor ? Math.round(margin(factor) * 100) : null,
    ownHours: hours,
    ownRevenue,
    avgRate: hours > 0 ? ownRevenue / hours : null,
    forecastedFactor: factor,
  };
};

/** CALC-16 by category — one finance category summed across the project's contracts. */
export type ProjectCategoryFinancials = {
  id: CategoryId;
  /** Σ amount over the contracts where the category is distributed (CALC-14's rule). */
  revenue: number;
  /** Σ catCost (CALC-9, CALC-20-aware) over the same contracts. */
  cost: number;
  /** revenue / cost; null while the category carries no cost. */
  budgetedFactor: number | null;
  marginPct: number | null;
  /** How many contracts the category is in use on. */
  contractCount: number;
  /** Own Work only: Σ ownHours (CALC-11) and revenue / hours. */
  hours?: number;
  avgRate?: number | null;
};

/**
 * CALC-16 by category — the project rollup split by finance category, for the
 * budget aside's "View by Category". A category is listed only where it is IN
 * USE: distributed (not hidden, not moved, available under the budget model,
 * CALC-19) on at least one contract. Own Work also carries the project's hours
 * and their average rate, the same two figures the headline shows. Categories
 * come back in CATEGORY_IDS order. Assumes one currency (BR-18).
 */
export const calculateProjectCategoryFinancials = (
  contracts: ContractInput[],
  blendedCostRate: number,
  options?: BudgetCalcOptions,
): ProjectCategoryFinancials[] =>
  CATEGORY_IDS.flatMap((id) => {
    const inUse = contracts.filter((contract) =>
      isDistributed(getCategory(contract.document, id), options),
    );
    if (inUse.length === 0) return [];
    const revenue = inUse.reduce(
      (sum, contract) => sum + getCategory(contract.document, id).amount,
      0,
    );
    const cost = inUse.reduce(
      (sum, contract) => sum + categoryCost(getCategory(contract.document, id), options),
      0,
    );
    const factor = cost > 0 ? revenue / cost : null;
    const row: ProjectCategoryFinancials = {
      id,
      revenue,
      cost,
      budgetedFactor: factor,
      marginPct: factor ? Math.round(margin(factor) * 100) : null,
      contractCount: inUse.length,
    };
    if (id !== 'own') return [row];
    const hours = inUse.reduce(
      (sum, contract) => sum + ownHours(contract.document, blendedCostRate, options),
      0,
    );
    return [{ ...row, hours, avgRate: hours > 0 ? revenue / hours : null }];
  });

/** CALC-21 by role — one role's allocated hours and cost over the scoped tasks. */
export type RoleAllocatedCost = {
  roleTlpId: string;
  roleName: string;
  /** Σ allocation hours on this role. */
  hours: number;
  /** Σ hours × the line's cost rate (the CALC-21 chain, person first). */
  cost: number;
  /** cost / hours — the role's average cost per hour, what the rows sort by. */
  costRate: number;
  /** How many staffing lines the role has. */
  lines: number;
};

/**
 * CALC-21 by role — ALLOCATED hours and cost grouped by the line's role, for
 * the contract summary's role pyramids. Only staffing lines count: a leaf's
 * estimate-only cost (no allocations) belongs to no role and is left out, so a
 * role appears only where somebody, or a role-only line, is actually
 * allocated. The line's cost rate is the same chain the rollup uses — the
 * named person's schedule first, then the role's price, then the contract's.
 *
 * `scope.contractId` limits the sum to the leaves mapped to that contract
 * (BR-4, explicit mapping only); without it the whole plan counts. Sorted by
 * COST PER HOUR, highest first — a 500/h role sits above a 400/h one whatever
 * their totals — ties by cost, then name. Iterative over the plan (BR-3).
 */
export const allocatedCostByRole = (
  project: ProjectDocument,
  contracts: ContractInput[],
  options?: PlanCalcOptions,
  scope?: { contractId?: string },
): RoleAllocatedCost[] => {
  const resolveCostRate = createCostRateResolver(contracts, project.blendedCostRate, options);
  const byRole = new Map<string, RoleAllocatedCost>();
  walkPlan(project.plan, (node) => {
    if (node.children.length > 0) return;
    const contractId = mappedContractId(node);
    if (scope?.contractId !== undefined && contractId !== scope.contractId) return;
    for (const allocation of node.allocations) {
      if (allocation.hours <= 0) continue;
      const key = allocation.roleRef.tlpId;
      const entry = byRole.get(key) ?? {
        roleTlpId: key,
        roleName: allocation.roleRef.snapshot.name,
        hours: 0,
        cost: 0,
        costRate: 0,
        lines: 0,
      };
      entry.hours += allocation.hours;
      entry.cost +=
        allocation.hours * resolveCostRate(contractId, allocation.roleRef, allocation.personRef);
      entry.lines += 1;
      byRole.set(key, entry);
    }
  });
  return [...byRole.values()]
    .map((row) => ({ ...row, costRate: row.cost / row.hours }))
    .sort(
      (a, b) => b.costRate - a.costRate || b.cost - a.cost || a.roleName.localeCompare(b.roleName),
    );
};

// ─── CALC-18: entry flows — pure (document, input) → document transforms (BR-21) ───

const withCategory = (
  document: ContractDocument,
  id: CategoryId,
  patch: Partial<CategoryBudget>,
): ContractDocument => ({
  ...document,
  categories: document.categories.map((category) =>
    category.id === id ? { ...category, ...patch } : category,
  ),
});

/**
 * Amount-first (fixed default): contingency seeded per the configured share
 * (BR-14; control 10%, 0 seeds it empty), Own Work is the residual, clamped ≥ 0.
 */
export const seedAmountFirst = (
  document: ContractDocument,
  input: { amount: number; exp?: number; sub?: number; mat?: number },
  options?: { contingencyShare?: number },
): ContractDocument => {
  const contingencyShare = options?.contingencyShare ?? DEFAULT_CONTINGENCY_SHARE;
  const cont = Math.round(input.amount * contingencyShare);
  const exp = Math.round(input.exp ?? 0);
  const sub = Math.round(input.sub ?? 0);
  const mat = Math.round(input.mat ?? 0);
  const own = Math.max(0, input.amount - cont - exp - sub - mat);

  let next: ContractDocument = { ...document, revenue: Math.round(input.amount) };
  next = withCategory(next, 'own', { amount: own, hidden: false });
  next = withCategory(next, 'cont', { amount: cont, ...(cont > 0 ? { hidden: false } : {}) });
  // A category that received budget is unhidden; a zero entry leaves visibility as-is.
  for (const [id, amount] of [
    ['exp', exp],
    ['sub', sub],
    ['mat', mat],
  ] as const) {
    next = withCategory(next, id, { amount, ...(amount > 0 ? { hidden: false } : {}) });
  }
  return next;
};

/** Hours-first (fixed): own from hours × cost rate × factor; contingency per share (BR-14). */
export const seedHoursFirst = (
  document: ContractDocument,
  input: { hours: number; blendedCostRate: number },
  options?: { contingencyShare?: number },
): ContractDocument => {
  const contingencyShare = options?.contingencyShare ?? DEFAULT_CONTINGENCY_SHARE;
  const ownFactor = getCategory(document, 'own').targetFactor;
  const own = Math.round(input.hours * input.blendedCostRate * ownFactor);
  const revenue = contingencyShare > 0 ? Math.round(own / (1 - contingencyShare)) : own;
  const cont = revenue - own;

  let next: ContractDocument = { ...document, revenue };
  next = withCategory(next, 'own', { amount: own, hidden: false, ownHoursOverride: input.hours });
  next = withCategory(next, 'cont', { amount: cont, ...(cont > 0 ? { hidden: false } : {}) });
  next = withCategory(next, 'exp', { amount: 0, hidden: true });
  next = withCategory(next, 'sub', { amount: 0, hidden: true });
  next = withCategory(next, 'mat', { amount: 0, hidden: true });
  return next;
};

/** T&M hours flow: revenue = hours × rate; own factor derives from the rate. */
export const seedTmHours = (
  document: ContractDocument,
  input: { hours: number; rate: number; blendedCostRate: number },
): ContractDocument => {
  const own = Math.round(input.hours * input.rate);
  const ownFactor =
    input.blendedCostRate > 0
      ? Number((input.rate / input.blendedCostRate).toFixed(2))
      : getCategory(document, 'own').targetFactor;

  let next: ContractDocument = { ...document, revenue: own };
  next = withCategory(next, 'own', {
    amount: own,
    hidden: false,
    targetFactor: Math.max(0.1, ownFactor),
    ownHoursOverride: input.hours,
  });
  next = withCategory(next, 'cont', { amount: 0, hidden: true });
  next = withCategory(next, 'exp', { amount: 0, hidden: true });
  next = withCategory(next, 'sub', { amount: 0, hidden: true });
  next = withCategory(next, 'mat', { amount: 0, hidden: true });
  return next;
};

/**
 * Cost-first entry (T&M "extras"): amount = cost × factor; revenue grows (CALC-14a).
 * `factorOverride: 1` is the uplift-off mode (CALC-20 — extras get no uplift).
 */
export const applyCostFirstEntry = (
  document: ContractDocument,
  input: { categoryId: Exclude<CategoryId, 'own' | 'cont'>; cost: number },
  options?: { factorOverride?: number },
): ContractDocument => {
  const category = getCategory(document, input.categoryId);
  const amount = Math.round(input.cost * (options?.factorOverride ?? category.targetFactor));
  const revenue = document.revenue - category.amount + amount;

  return withCategory({ ...document, revenue }, input.categoryId, { amount, hidden: false });
};
